import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import {
	ActivityIcon,
	CalendarIcon,
	CompassIcon,
	ConversationIcon,
	DialogueIcon,
	DraftsIcon,
	GearIcon,
	GridIcon,
	HomeIcon,
	LibraryIcon,
	StarsIcon,
	TargetIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Sidebar } from '@treams/treams-ui';

import RecommendCard from './RecommendCard';

export default function PersonalNav() {
	const { t } = useTranslation();

	const hasGoals = useFeature(graphql.FeatureName.Goals);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasLearning = useFeature(graphql.FeatureName.Learning);
	const hasNotes = useFeature(graphql.FeatureName.Notes);
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);
	const hasTalentReviews = useFeature(graphql.FeatureName.TalentReviews);
	const hasTalentReviewsV2 = useFeature(graphql.FeatureName.TalentReviewsV2);
	const usesManagerMode = useFeature(graphql.FeatureName.Helicopter);
	const hasGrowthPath = useFeature(graphql.FeatureName.GrowPath);
	const hasJobCompetenciesExplorer = useFeature(graphql.FeatureName.JobCompetenciesExplorer);
	// This flag is used for approval of more advanced features (AI Agent, AI Conversation Insights)
	const hasAiConversationInsights = useFeature(graphql.FeatureName.ConversationAiInsights);
	const hasAiAgent = useFeature(graphql.FeatureName.AiAgent);

	const {
		isWorkspaceSuperuser,
		isGroupManager,
		isAdministrator,
		isEngagementOfficer,
		isHRBusinessPartner,
		isPrimaryGroupManager,
	} = useViewerRoles();

	return (
		<>
			<Sidebar.Item
				icon={<HomeIcon />}
				title={t('sidebar.startpage')}
				path={['/', '/inbox/manager', '/journey']}
				exact
			/>
			{hasGoals && <Sidebar.Item icon={<TargetIcon />} title={t('sidebar.goals')} path="/goals" />}
			{hasFeedback && (
				<Sidebar.Item icon={<DialogueIcon />} title={t('sidebar.feedback')} path="/feedback" />
			)}
			{hasGrowthPath && (
				<Sidebar.Item
					icon={<CompassIcon />}
					title={hasJobCompetenciesExplorer ? t('sidebar.growthPath') : t('sidebar.competencies')}
					path="/growth-path"
				/>
			)}
			{hasConversations && (
				<Sidebar.Item
					icon={<ConversationIcon />}
					title={t('sidebar.conversations')}
					path="/conversations"
				/>
			)}
			{!usesManagerMode && hasTalentReviews && (isWorkspaceSuperuser || isGroupManager) && (
				<Sidebar.Item
					icon={<GridIcon />}
					title={t('sidebar.talentReview')}
					path={hasTalentReviewsV2 ? '/talent-reviews/v2' : '/talent-reviews'}
				/>
			)}
			{hasNotes && (
				<Sidebar.Item
					icon={hasOneOnOnes ? <CalendarIcon /> : <DraftsIcon />}
					title={hasOneOnOnes ? t('sidebar.oneOnOnes') : t('sidebar.notes')}
					path="/notes"
				/>
			)}
			{hasLearning && (
				<Sidebar.Item icon={<LibraryIcon />} title={t('sidebar.learning')} path="/learning" />
			)}
			{(isWorkspaceSuperuser || (hasAiConversationInsights && hasAiAgent)) && (
				<Sidebar.Item icon={<StarsIcon />} title={t('sidebar.aiAgent')} path="/assistant" />
			)}

			<Sidebar.Spacing />

			<Box flexGrow={1} flexShrink={1} minHeight={0} display={['none', 'none', 'block']} />

			{(isPrimaryGroupManager || isHRBusinessPartner || isEngagementOfficer) && (
				<Sidebar.Item icon={<ActivityIcon />} title={t('sidebar.analytics')} path="/analytics" />
			)}
			{(isWorkspaceSuperuser || isAdministrator || isEngagementOfficer || isHRBusinessPartner) && (
				<Sidebar.Item icon={<GearIcon />} title={t('sidebar.manage')} path="/settings" />
			)}
			<RecommendCard />
		</>
	);
}
